import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/03a587a1be67a3ab52d2d9f34cb399b8.jpg"
            alt="تواصل معنا"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/75"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-6xl md:text-7xl font-black text-white mb-6">تواصل معنا</h1>
            <p className="text-2xl text-accent font-bold">نحن هنا لخدمتك على مدار الساعة</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              {
                icon: 'ri-phone-fill',
                title: 'اتصل بنا',
                info: '920000000',
                link: 'tel:920000000',
                color: 'bg-green-500',
              },
              {
                icon: 'ri-whatsapp-fill',
                title: 'واتساب',
                info: '+966 50 000 0000',
                link: 'https://wa.me/966500000000',
                color: 'bg-green-600',
              },
              {
                icon: 'ri-mail-fill',
                title: 'البريد الإلكتروني',
                info: 'info@rawafee.sa',
                link: 'mailto:info@rawafee.sa',
                color: 'bg-blue-500',
              },
              {
                icon: 'ri-map-pin-fill',
                title: 'الموقع',
                info: 'الرياض، المملكة العربية السعودية',
                link: '#map',
                color: 'bg-red-500',
              },
            ].map((contact, index) => (
              <motion.a
                key={index}
                href={contact.link}
                target={contact.link.startsWith('http') ? '_blank' : undefined}
                rel={contact.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className={`w-20 h-20 flex items-center justify-center ${contact.color} rounded-2xl mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${contact.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">{contact.title}</h3>
                <p className="text-gray-600 font-semibold">{contact.info}</p>
              </motion.a>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl"
            >
              <h2 className="text-4xl font-black text-primary mb-4">أرسل لنا رسالة</h2>
              <p className="text-gray-600 mb-8">املأ النموذج وسنتواصل معك في أقرب وقت</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-primary font-bold mb-2">الاسم الكامل *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-primary font-bold mb-2">البريد الإلكتروني *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-primary font-bold mb-2">رقم الجوال *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base"
                      placeholder="05xxxxxxxx"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-primary font-bold mb-2">اسم الشركة</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base"
                    placeholder="اسم شركتك (اختياري)"
                  />
                </div>

                <div>
                  <label className="block text-primary font-bold mb-2">نوع المشروع *</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base cursor-pointer"
                  >
                    <option value="">اختر نوع المشروع</option>
                    <option value="residential">سكني</option>
                    <option value="commercial">تجاري</option>
                    <option value="industrial">صناعي</option>
                    <option value="infrastructure">بنية تحتية</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>

                <div>
                  <label className="block text-primary font-bold mb-2">تفاصيل المشروع *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-accent focus:outline-none transition-all text-base resize-none"
                    placeholder="أخبرنا عن مشروعك واحتياجاتك..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">الحد الأقصى: 500 حرف</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-accent hover:bg-accent-dark text-primary px-8 py-4 rounded-lg font-black text-lg transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
                >
                  <span>إرسال الرسالة</span>
                  <i className="ri-send-plane-fill text-xl"></i>
                </button>
              </form>
            </motion.div>

            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Working Hours */}
              <div className="bg-primary rounded-3xl p-8 text-white">
                <div className="w-16 h-16 flex items-center justify-center bg-accent rounded-2xl mb-6">
                  <i className="ri-time-fill text-3xl text-primary"></i>
                </div>
                <h3 className="text-3xl font-black mb-6">ساعات العمل</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-white/20">
                    <span className="font-bold">السبت - الخميس</span>
                    <span className="text-accent font-bold">8:00 ص - 6:00 م</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-white/20">
                    <span className="font-bold">الجمعة</span>
                    <span className="text-gray-400">مغلق</span>
                  </div>
                  <div className="flex items-center gap-3 bg-accent/20 p-4 rounded-xl mt-6">
                    <i className="ri-customer-service-2-fill text-2xl text-accent"></i>
                    <div>
                      <p className="font-bold">الدعم الفني</p>
                      <p className="text-sm text-gray-300">متاح 24/7</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-white rounded-3xl p-8 shadow-xl">
                <h3 className="text-3xl font-black text-primary mb-6">تواصل سريع</h3>
                <div className="space-y-4">
                  <a
                    href="tel:920000000"
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-accent/10 transition-all group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-lg group-hover:scale-110 transition-transform">
                      <i className="ri-phone-fill text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">اتصل الآن</p>
                      <p className="text-lg font-bold text-primary">920000000</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/966500000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-accent/10 transition-all group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-lg group-hover:scale-110 transition-transform">
                      <i className="ri-whatsapp-fill text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">واتساب</p>
                      <p className="text-lg font-bold text-primary">+966 50 000 0000</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@rawafee.sa"
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-accent/10 transition-all group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-lg group-hover:scale-110 transition-transform">
                      <i className="ri-mail-fill text-2xl text-white"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">البريد الإلكتروني</p>
                      <p className="text-lg font-bold text-primary">info@rawafee.sa</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-gradient-to-br from-accent to-accent-dark rounded-3xl p-8">
                <h3 className="text-3xl font-black text-primary mb-6">تابعنا</h3>
                <div className="flex gap-4">
                  {[
                    { icon: 'ri-twitter-x-fill', link: '#' },
                    { icon: 'ri-linkedin-fill', link: '#' },
                    { icon: 'ri-instagram-fill', link: '#' },
                    { icon: 'ri-facebook-fill', link: '#' },
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 flex items-center justify-center bg-primary hover:bg-primary-dark rounded-xl transition-all hover:scale-110 cursor-pointer"
                    >
                      <i className={`${social.icon} text-2xl text-accent`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="map" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-primary mb-4">موقعنا</h2>
            <p className="text-xl text-gray-600">الرياض، المملكة العربية السعودية</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden shadow-2xl"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.2!2d46.7!3d24.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzAwLjAiTiA0NsKwNDInMDAuMCJF!5e0!3m2!1sen!2ssa!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="موقع الشركة"
            ></iframe>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;